import { Form, Row, Col } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { errorAlert, successAlert } from '../../components/toastr';
import { SubmitButton, CancelButton } from '../../components/btns';
import { getClients, } from '../../resources/api/clients';
import { Link, useNavigate } from 'react-router-dom';
import SelectInput from '../../components/select';
import { PageLoading } from '../../components/loading';
import { createProject, updateProject } from '../../resources/api/projects';
import { DateSelector } from '../../components/datetimepicker';
import { billingCycles, billingPeriods } from '../../assets/constants';

const { Control, Control: { Feedback }, Label } = Form;

/**
 * handle page for creating trip
 * @param {Object} props
 * @param {"edit"|"create"} props.action
 * @param {import('../../resources/api/projects').ProjectObject} props.details
 * @param {(details: import('../../resources/api/projects').ProjectObject) => void} props.onSubmit
 */
const ProjectForm = ({ details: projectObject, action, onSubmit }) => {


    const [details, setDetails] = useState(projectObject);
    const [clients, setClients] = useState([]);

    const [validated, setValidated] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [isSubmitting, setSubmitting] = useState(false);

    const navigate = useNavigate();

    /**
     * handle the overall submitting of the form
     * @param {React.FormEvent} e
     */
    const handleSubmit = e => {
        const form = e.currentTarget;

        e.preventDefault();

        if (!form.checkValidity()) {
            setValidated(true);
            errorAlert("You have errors in your form. These have been highlighted for you.", "Form Errors");
            return;
        }

        setValidated(false);
        setSubmitting(true);

        const promise = (action === "edit") ? updateProject(details, details.id) : createProject({ ...details });

        promise
            .then(({ project, message }) => {
                successAlert(message);
                onSubmit(project);
            })
            .catch(e => {
                errorAlert(e);
                setSubmitting(false);
            })
    }


    useEffect(() => {
        getClients()
            .then(({ clients }) => {
                setClients(
                    clients.map(c => ({
                        ...c,
                        description: c.address
                    }))
                );

            })
            .catch(e => errorAlert(e))
            .finally(() => setLoaded(true));
    }, []);


    if (!loaded) return <PageLoading>Loading clients...</PageLoading>;

    return (
        <Form className="max-800" validated={validated} noValidate onSubmit={handleSubmit}>
            {/* <h4 className="form-section-label">Details</h4> */}
            <Row>
                <Col sm={6} className="my-1">
                    <Label className="form-field-title">Client</Label>
                    <SelectInput
                        value={[details.client_id]}
                        onChange={([id]) => setDetails(d => ({ ...d, client_id: id || "", client_contact_id: "", project_id: "" }))}
                        options={clients}
                        required
                        placeholder='-'
                        maxItems={1}
                        errorText="This field is required"
                    />
                    <Form.Text muted style={{ lineHeight: 1, display: "block" }}>
                        The client must exist in the existing lists of clients.
                        <Link to="/app/clients/new?action=create_job">Click here</Link> to add a new client.
                    </Form.Text>
                </Col>
            </Row>
            <Row>
                <Col sm={6} className="my-1">
                    <Label className="form-field-title">Title</Label>
                    <Control
                        value={details.title}
                        onChange={e => setDetails({ ...details, title: e.currentTarget.value })}
                        required={true}
                    />
                    <Feedback type="invalid">
                        The title is required.
                    </Feedback>
                    <Form.Text muted>
                        This can be the client's full name or company/organisation title.
                    </Form.Text>
                </Col>
                <Col sm={6} className="my-1">
                    <Label className="form-field-title">Code</Label>
                    <Control
                        value={details.code}
                        onChange={e => setDetails({ ...details, code: e.currentTarget.value })}
                        required={true}
                    />
                    <Feedback type="invalid">
                        The code is required.
                    </Feedback>
                </Col>
            </Row>

            <Row className="g-2">
                <Col xs={6} sm={4} className="my-1">
                    <Label className="form-field-title">Start Date</Label>
                    <DateSelector
                        value={details.start_date}
                        onChange={date =>
                            setDetails(d => ({
                                ...d,
                                start_date: date,
                                end_date: (!d.end_date || (d.end_date < date)) ? date : d.end_date
                            }))}
                        // placeholder="when is the event taking place?"
                        required={true}
                    // minDate={(profile.permission_level === 1) ? false : "2021-01-01"}

                    />
                    <Feedback type="invalid">
                        A valid date must be provided.
                    </Feedback>
                </Col>
                <Col xs={6} sm={4} className="my-1">
                    <Label className="form-field-title">End Date</Label>
                    <DateSelector
                        value={details.end_date}
                        onChange={date =>
                            setDetails(d => ({
                                ...d,
                                end_date: date
                            }))}
                        // placeholder="when is the event taking place?"
                        required={true}
                        minDate={details.start_date}

                    />
                    <Feedback type="invalid">
                        A valid date must be provided.
                    </Feedback>
                </Col>
            </Row>

            <Row className="g-2">
                <Col xs={6} sm={4} className="my-1">
                    <Label className="form-field-title">Billing Cycle</Label>
                    <Form.Select
                        value={details.billing_mode}
                        onChange={e => setDetails({ ...details, billing_mode: e.currentTarget.value })}
                        required
                    >
                        <option value=""> Select An Item</option>
                        {billingCycles.map(b => <option key={b.id} value={b.id}>{b.title}</option>)}
                    </Form.Select>
                    <Feedback type="invalid">
                        A valid date must be provided.
                    </Feedback>
                </Col>
                <Col xs={6} sm={4} className="my-1">
                    <Label className="form-field-title">Billing Period</Label>
                    <Form.Select
                        value={details.billing_cycle}
                        onChange={e => setDetails({ ...details, billing_cycle: e.currentTarget.value })}
                        required
                    >
                        <option value=""> Select An Item</option>
                        {billingPeriods.map(b => <option key={b} value={b}>{b.replace(/_/g, " ").toUpperCase()}</option>)}
                    </Form.Select>
                    <Feedback type="invalid">
                        A valid date must be provided.
                    </Feedback>
                </Col>
            </Row>


            <Row>
                <Col className="mt-4 mb-3 text-end">
                    <SubmitButton isSubmitting={isSubmitting} type="submit">
                        {action === "edit" ? "Update Client" : "Create Client"}
                    </SubmitButton>
                    <CancelButton isSubmitting={isSubmitting} onClick={() => navigate(-1)}>
                        Cancel
                    </CancelButton>
                </Col>
            </Row>
        </Form>
    )
}


export default ProjectForm;